<script setup lang="ts">
import { computed, ref } from 'vue'
import { Loader2 } from '@lucide/vue'
import { useFormControl } from '../../composables/useFormControl'
import { useBoundValue } from '../../composables/useBoundValue'
const props = withDefaults(defineProps<{
  checked?: boolean
  defaultChecked?: boolean
  disabled?: boolean
  loading?: boolean
  checkedChildren?: string
  unCheckedChildren?: string
  size?: 'default' | 'small'
}>(), { checked: undefined, defaultChecked: false, disabled: undefined, loading: false, checkedChildren: '', unCheckedChildren: '' })
const emit = defineEmits<{
  'update:checked': [checked: boolean]
  change: [checked: boolean, event: MouseEvent]
  click: [checked: boolean, event: MouseEvent]
}>()
const { disabled, size: formSize, controlAttrs, onFieldChange, onFieldBlur } = useFormControl({ get disabled() { return props.disabled } })
const size = computed(() => props.size ?? (formSize.value === 'small' ? 'small' : 'default'))
const inputRef = ref<HTMLButtonElement | null>(null)
const innerChecked = ref(props.defaultChecked)
const mergedChecked = useBoundValue(props, 'checked', innerChecked)
const isChecked = computed(() => Boolean(mergedChecked.value))
const isDisabled = computed(() => disabled.value || props.loading)
function toggle(event: MouseEvent) {
  if (isDisabled.value) return
  const next = !isChecked.value
  innerChecked.value = next
  emit('update:checked', next)
  emit('change', next, event)
  emit('click', next, event)
  return onFieldChange()
}
defineExpose({ focus: (options?: FocusOptions) => inputRef.value?.focus(options), blur: () => inputRef.value?.blur() })
</script>

<template>
  <button
    ref="inputRef"
    v-bind="controlAttrs()"
    type="button"
    role="switch"
    :aria-checked="isChecked"
    :disabled="isDisabled"
    class="rr-switch"
    :class="[
      `rr-switch--${size}`,
      {
        'is-checked': isChecked,
        'is-disabled': isDisabled,
        'is-loading': loading
      }
    ]"
    @click="toggle"
    @blur="onFieldBlur"
  >
    <div class="rr-switch__handle">
      <Loader2 v-if="loading" class="rr-switch__loading" :size="10" aria-hidden="true" />
    </div>
    <span v-if="checkedChildren || unCheckedChildren" class="rr-switch__inner">
      {{ isChecked ? checkedChildren : unCheckedChildren }}
    </span>
  </button>
</template>

<style scoped>
.rr-switch {
  position: relative;
  display: inline-flex;
  align-items: center;
  min-width: 44px;
  height: 22px !important;
  min-height: 22px !important;
  max-height: 22px !important;
  padding: 0 4px !important;
  margin: 0 !important;
  box-sizing: border-box !important;
  border: 0 !important;
  border-radius: 100px !important;
  background: var(--rr-color-border-strong);
  color: var(--rr-color-surface);
  cursor: pointer;
  user-select: none;
  transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
  vertical-align: middle;
  line-height: 1 !important;
  font-weight: normal !important;
}

.rr-switch.is-checked {
  background: var(--rr-color-primary) !important;
  color: var(--rr-color-surface) !important;
}

.rr-switch.is-disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.rr-switch__handle {
  position: absolute;
  top: 2px;
  left: 2px;
  display: grid;
  place-items: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--rr-color-surface);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
}

.rr-switch.is-checked .rr-switch__handle {
  left: calc(100% - 20px);
}

.rr-switch__inner {
  display: inline-block;
  margin-left: 20px;
  margin-right: 4px;
  color: var(--rr-color-surface);
  font-size: 11px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  transition: margin 200ms cubic-bezier(0.4, 0, 0.2, 1);
}

.rr-switch.is-checked .rr-switch__inner {
  margin-left: 4px;
  margin-right: 20px;
}

/* Small size */
.rr-switch--small {
  min-width: 28px;
  height: 16px !important;
  min-height: 16px !important;
  max-height: 16px !important;
}
.rr-switch--small .rr-switch__handle {
  width: 12px;
  height: 12px;
}
.rr-switch--small.is-checked .rr-switch__handle {
  left: calc(100% - 14px);
}

.rr-switch__loading {
  animation: rr-spin 800ms linear infinite;
  color: var(--rr-color-primary);
}

@keyframes rr-spin {
  to { transform: rotate(360deg); }
}
</style>
